/**
 * en2es / es2en — canonical EN↔ES route mappings.
 *
 * The site uses dedicated Spanish route components (e.g., /es/negocio-local
 * is rendered by SpanishLocalBusiness.js, separate from the English
 * /local-business → LocalBusinessLanding.js). The English components are NOT
 * i18n-translated, so flipping the i18next locale in place leaves them in
 * English. To honor the language toggle, LanguageToggle navigates to the
 * mapped sibling route using this table.
 *
 * Add to this map any time you ship a new EN ↔ ES pair.
 */
const EN_TO_ES = {
  // Service pages
  '/': '/es',
  '/local-business': '/es/negocio-local',
  '/ai-search': '/es/busqueda-ai',
  '/enterprise': '/es/empresas',
  '/digital-marketing-platform': '/es/plataforma-marketing-digital',
  '/personal-portfolio': '/es/portafolio-personal',
  '/gift-portfolio': '/es/regalo-portafolio',
  '/mobile-apps': '/es/aplicaciones-moviles',
  '/bilingual-addon': '/es/complemento-bilingue',
  '/dealers': '/es/concesionarios',
  '/map-scan': '/es/map-scan',
  '/map-dominator': '/es/mapa-dominador',
  // Social media (multiple aliases — canonical is /social-media-management)
  '/social': '/es/redes-sociales',
  '/social-media': '/es/redes-sociales',
  '/social-pricing': '/es/redes-sociales',
  '/social-media-pricing': '/es/redes-sociales',
  '/social-media-management': '/es/redes-sociales',
  // Pricing (multiple aliases — canonical is /pricing)
  '/services': '/es/precios',
  '/packages': '/es/precios',
  '/seo-packages': '/es/precios',
  '/pricing': '/es/precios',
  // Contact (multiple aliases — canonical is /contact)
  '/consultation': '/es/contacto',
  '/book-consultation': '/es/contacto',
  '/contact.php': '/es/contacto',
  '/contact': '/es/contacto',
  '/agency/register': '/es/socio/postular',
  // Driver
  '/driver': '/es/conductor',
  // Content + meta
  '/about.php': '/es/sobre-nosotros',
  '/about': '/es/sobre-nosotros',
  '/faq': '/es/preguntas-frecuentes',
  '/new-business': '/es/nuevo-negocio',
  '/new-business-offer': '/es/nuevo-negocio',
  '/press': '/es/prensa',
  '/press-releases': '/es/prensa',
  '/privacy': '/es/privacidad',
  '/terms': '/es/terminos',
  '/sms-terms': '/es/sms-terminos',
  '/blog.php': '/es/blog',
  '/blog': '/es/blog',
  // Partner program (multiple aliases — canonical is /partner)
  '/refer': '/es/socio',
  '/partners': '/es/socio',
  '/partner-landing': '/es/socio',
  '/affiliate-landing': '/es/socio',
  '/affiliate': '/es/socio',
  '/partner': '/es/socio',
  '/become-affiliate': '/es/socio/postular',
  '/become-a-partner': '/es/socio/postular',
  '/partner/apply': '/es/socio/postular',
  '/partner/terms': '/es/socio/terminos',
  '/affiliate/terms': '/es/socio/terminos',
  '/portfolio.php': '/es/socio/portafolio',
  '/portfolio': '/es/socio/portafolio',
  '/partner/portfolio': '/es/socio/portafolio',
  '/affiliate/portfolio': '/es/socio/portafolio',
  // AI visibility (all aliases for the demo)
  '/ai-visibility-demo': '/es/demo-visibilidad-ai',
  '/ai-visibility-check': '/es/demo-visibilidad-ai',
  '/ai-visibility-trial': '/es/demo-visibilidad-ai',
  '/ai-check': '/es/demo-visibilidad-ai',
  // Local branding (external redirect both sides)
  '/local-branding': '/es/local-branding',
  // Tipsheets
  '/2026-kentucky-derby-churchill-downs-race-card-tip-sheet': '/es/2026-kentucky-derby-pronosticos-ia',
  '/hipodromo-lone-star-park-tip-sheet': '/es/hipodromo-lone-star-park-pronosticos-ia',
  '/2026-preakness-stakes-pimlico-tip-sheet': '/es/2026-preakness-stakes-pronosticos-ia',
  '/2026-belmont-stakes-saratoga-tip-sheet': '/es/2026-belmont-stakes-pronosticos-ia',
  '/2026-kentucky-derby-results': '/es/2026-kentucky-derby-resultados',
  '/lone-star-park-results': '/es/lone-star-park-resultados',
};

// Auto-derive the reverse map
const ES_TO_EN = Object.fromEntries(
  Object.entries(EN_TO_ES).map(([en, es]) => [es, en])
);

/** Find the ES counterpart for an EN pathname, or null if unmapped. */
export function en2es(pathname) {
  if (!pathname) return null;
  // Normalize trailing slash
  const p = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return EN_TO_ES[p] || null;
}

/** Find the EN counterpart for an ES pathname, or '/' as safe fallback. */
export function es2en(pathname) {
  if (!pathname) return '/';
  const p = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return ES_TO_EN[p] || '/';
}

export { EN_TO_ES, ES_TO_EN };
